'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import {
  ArrowRight,
  Package,
  Cloud,
  Container,
  Database,
  ShieldCheck,
  Eye,
  Lock,
  Server,
} from 'lucide-react';
import Link from 'next/link';
import { softwareApplicationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const paths = [
  {
    icon: Package,
    name: 'Node.js SDK',
    tag: 'Apps with a server',
    color: 'text-rose-400',
    desc: 'One line in Next.js, Express or any Node backend. Silker runs inside your app and blocks threats locally, before they reach your model or database.',
    best: 'Cursor / Claude Code / Bolt full-stack / Replit',
  },
  {
    icon: Container,
    name: 'Docker proxy',
    tag: 'Self-host · any stack',
    color: 'text-blue-400',
    desc: 'Run the proxy in front of any backend - PHP, Java, Python, Go. Point your traffic at it. No Cloudflare required.',
    best: 'Self-hosted / regulated environments',
  },
  {
    icon: Cloud,
    name: 'Cloudflare Worker',
    tag: 'Coming soon',
    color: 'text-orange-400',
    desc: 'Deploy a Worker on your Cloudflare zone. Traffic is inspected at the edge before it hits your app - no changes to your codebase.',
    best: 'Any app behind a custom domain',
  },
];

const architectureBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Architecture', url: `${SITE_URL}/architecture` },
]);

export default function ArchitecturePage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(architectureBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                Architecture
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5 text-white">
                How Silker works
              </h1>
              <p className="text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                Silker inspects traffic where your app runs and blocks threats in real time. Detection runs on your own
                infrastructure - we only receive sanitized security events.
              </p>
            </div>

            {/* WAF vs Silker comparison */}
            <WafVsSilker />

            {/* Request flow diagram */}
            <div
              className="rounded-none border border-white/10 bg-neutral-900/30 p-6 sm:p-10 mb-6"
              data-scroll-animate="fade-up"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-8 text-center">
                Request flow
              </div>

              <div className="flex flex-col lg:flex-row items-stretch justify-center gap-4">
                <FlowBox icon={Server} label="Incoming request" sub="user / attacker / bot" tone="neutral" />
                <FlowArrow />
                <FlowBox icon={ShieldCheck} label="Silker inspects" sub="runs on your infra" tone="rose" />
                <FlowArrow />
                <div className="flex flex-col gap-3 justify-center">
                  <div className="flex items-center gap-3 px-4 py-3 border border-emerald-500/30 bg-emerald-500/5">
                    <span className="text-emerald-400 text-sm font-medium whitespace-nowrap">Legitimate → your app</span>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3 border border-rose-500/30 bg-rose-500/5">
                    <span className="text-rose-400 text-sm font-medium whitespace-nowrap">Malicious → 403 blocked</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 mt-8 pt-8 border-t border-white/5 text-sm text-neutral-400">
                <Eye className="w-4 h-4 text-neutral-500" />
                Sanitized security events stream to your dashboard - your traffic never passes through our servers.
              </div>
            </div>

            {/* What we catch */}
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-20" data-scroll-animate="fade-up">
              {[
                'Prompt injection & jailbreaks on your LLM routes',
                'Sensitive data & secrets leaking out (DLP)',
                'API abuse, brute force & rate-limit violations',
                'OWASP basics: SQLi, XSS, path traversal, file-upload abuse',
              ].map((item, i) => (
                <div key={i} className="bg-neutral-950 p-5 flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-neutral-300">{item}</span>
                </div>
              ))}
            </div>

            {/* Three delivery paths */}
            <div className="text-center mb-10" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-semibold text-white tracking-tight">Three ways to deploy the guard</h2>
              <p className="mt-3 text-neutral-400 max-w-2xl mx-auto">
                Same detection engine. Where it sits depends on where your app runs.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-6" data-scroll-animate="fade-up">
              {paths.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="bg-neutral-950 p-6 flex flex-col">
                    <Icon className={`w-7 h-7 ${p.color} mb-4`} />
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white">{p.name}</h3>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
                      {p.tag}
                    </span>
                    <p className="text-sm text-neutral-400 leading-relaxed flex-1">{p.desc}</p>
                    <div className="mt-4 pt-4 border-t border-white/5 text-xs text-neutral-500">
                      Best for: <span className="text-neutral-300">{p.best}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Honest note about Supabase-direct apps */}
            <div
              className="rounded-none border border-white/10 bg-neutral-900/30 p-6 sm:p-8 mb-20 flex items-start gap-4"
              data-scroll-animate="fade-up"
            >
              <div className="p-2 border border-amber-500/30 bg-amber-500/5 shrink-0">
                <Database className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-medium mb-1">Apps with no backend (browser → Supabase)</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  Some AI-built apps talk to Supabase directly from the browser, with no server in between. There is no
                  place to put an inline guard there - and blocking in the browser can be bypassed. For those apps the
                  honest answer is different: we lock the database down (Row-Level Security) and monitor access, rather
                  than pretend to block live. We&apos;ll always tell you which protection actually applies to your setup.
                </p>
              </div>
            </div>

            {/* Privacy */}
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-20" data-scroll-animate="fade-up">
              <div className="bg-neutral-950 p-6">
                <Lock className="w-6 h-6 text-blue-400 mb-4" />
                <h3 className="text-white font-medium mb-2">Traffic stays with you</h3>
                <p className="text-sm text-neutral-400">Detection runs in your runtime or your edge. Your requests never route through our servers.</p>
              </div>
              <div className="bg-neutral-950 p-6">
                <Eye className="w-6 h-6 text-blue-400 mb-4" />
                <h3 className="text-white font-medium mb-2">Only events leave</h3>
                <p className="text-sm text-neutral-400">We receive sanitized security events (what was blocked and why) - not your users&apos; payloads.</p>
              </div>
              <div className="bg-neutral-950 p-6">
                <ShieldCheck className="w-6 h-6 text-blue-400 mb-4" />
                <h3 className="text-white font-medium mb-2">PII redacted first</h3>
                <p className="text-sm text-neutral-400">Sensitive data is redacted before anything is logged or sent.</p>
              </div>
            </div>

            <div className="text-center" data-scroll-animate="fade-up">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/docs"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors"
                >
                  Read the docs
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/#scan"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white border border-white/10 rounded-none text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  Scan your app - free
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function WafVsSilker() {
  const wafCan = [
    'Block known bad IPs & botnets',
    'Rate-limit by IP address',
    'Block common attack signatures',
    'DDoS mitigation at network level',
  ];
  const wafCannot = [
    'See inside encrypted payloads',
    'Inspect prompt injection in JSON body',
    'Detect IDOR (valid request, wrong object)',
    'Inspect outbound API responses',
    'Understand application-level auth logic',
    'Catch data leaking in your API responses',
    'Analyze LLM inputs and outputs',
  ];

  const layers = [
    { label: 'Internet',          sub: 'user / bot / attacker',    waf: false, silker: false, color: 'neutral' },
    { label: 'CDN / WAF',         sub: 'Cloudflare, AWS Shield',   waf: true,  silker: false, color: 'amber' },
    { label: 'Load balancer',     sub: 'nginx, reverse proxy',     waf: false, silker: false, color: 'neutral' },
    { label: 'Your app runtime',  sub: 'Next.js / Express / Node', waf: false, silker: true,  color: 'rose' },
    { label: 'Database / LLM',   sub: 'Postgres, OpenAI, etc.',   waf: false, silker: false, color: 'neutral' },
  ];

  return (
    <div className="mb-20" data-scroll-animate="fade-up">
      <div className="text-center mb-10">
        <div className="inline-block px-3 py-1 text-xs font-semibold tracking-widest uppercase text-rose-400 border border-rose-500/30 bg-rose-500/5 mb-4">
          Why WAF is not enough
        </div>
        <h2 className="text-3xl font-semibold text-white tracking-tight mb-3">
          WAF protects the door.<br/>Silker protects the house.
        </h2>
        <p className="text-neutral-400 max-w-xl mx-auto text-sm leading-relaxed">
          A WAF sits at the network edge - it sees IP addresses and HTTP headers.
          It cannot see inside your app. Silker runs in your runtime, where the real attacks happen.
        </p>
      </div>

      {/* Stack layers diagram */}
      <div className="border border-white/10 bg-neutral-950 p-6 sm:p-8 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-6 text-center">Request path - where protection sits</div>
        <div className="max-w-2xl mx-auto space-y-2">
          {layers.map((layer, i) => (
            <div key={i} className="flex items-center gap-3">
              {/* Layer label */}
              <div className={`flex-1 flex items-center gap-3 px-4 py-3 border ${
                layer.color === 'amber' ? 'border-amber-500/30 bg-amber-500/5' :
                layer.color === 'rose'  ? 'border-rose-500/40 bg-rose-500/8' :
                'border-white/5 bg-black/20'
              }`}>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${
                    layer.color === 'amber' ? 'text-amber-300' :
                    layer.color === 'rose'  ? 'text-rose-300' : 'text-neutral-300'
                  }`}>{layer.label}</div>
                  <div className="text-xs text-neutral-600 mt-0.5">{layer.sub}</div>
                </div>
                {/* Badges */}
                <div className="flex gap-2 shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-none border ${
                    layer.waf
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                      : 'border-white/5 bg-white/2 text-neutral-700'
                  }`}>WAF</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-none border ${
                    layer.silker
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-400'
                      : 'border-white/5 bg-white/2 text-neutral-700'
                  }`}>Silker</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-6 pt-5 border-t border-white/5 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-amber-500/20 border border-amber-500/40 inline-block"></span> Protects at this layer</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-white/3 border border-white/10 inline-block"></span> Blind at this layer</span>
        </div>
      </div>

      {/* What WAF can / cannot */}
      <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {/* WAF can */}
        <div className="bg-neutral-950 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
            <span className="text-sm font-semibold text-amber-300">WAF protects against</span>
          </div>
          <ul className="space-y-2">
            {wafCan.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-neutral-400">
                <span className="text-amber-500 mt-0.5 shrink-0">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        {/* WAF cannot */}
        <div className="bg-neutral-950 p-6 border-t md:border-t-0 md:border-l border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-rose-400 rounded-full"></span>
            <span className="text-sm font-semibold text-rose-300">WAF cannot see - Silker can</span>
          </div>
          <ul className="space-y-2">
            {wafCannot.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-neutral-400">
                <span className="text-rose-500 mt-0.5 shrink-0">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FlowBox({
  icon: Icon,
  label,
  sub,
  tone,
}: {
  icon: typeof Server;
  label: string;
  sub: string;
  tone: 'neutral' | 'rose';
}) {
  const border = tone === 'rose' ? 'border-rose-500/40 bg-rose-500/5' : 'border-white/10 bg-black/40';
  const iconColor = tone === 'rose' ? 'text-rose-400' : 'text-neutral-400';
  return (
    <div className={`flex flex-col items-center justify-center text-center px-6 py-5 border ${border} min-w-[180px]`}>
      <Icon className={`w-6 h-6 ${iconColor} mb-2`} />
      <div className="text-sm font-medium text-white">{label}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center text-neutral-700">
      <ArrowRight className="w-5 h-5 rotate-90 lg:rotate-0" />
    </div>
  );
}
